import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { publicLeadsApi } from '../../services/api';
import { resolveClinicLogoSrc } from '../../utils/clinicLogo';
import { todayYmdIST } from '../../utils/timezoneDisplay';
import { HiCheckCircle, HiExclamationCircle } from 'react-icons/hi';

const fieldClass = 'input py-2 min-h-[40px] text-sm';
const labelClass = 'block text-xs font-medium text-gray-700 mb-1';

const SectionBlock = ({ title, children }) => (
  <section>
    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{title}</h3>
    {children}
  </section>
);

const PageShell = ({ children }) => (
  <div className="min-h-[100dvh] bg-slate-50 px-2 pb-2 pt-[max(0.5rem,env(safe-area-inset-top))] sm:px-3 sm:pb-3">
    <div className="mx-auto w-full max-w-2xl">{children}</div>
  </div>
);

const FormCardHeader = ({ meta, subtitle }) => {
  const logoSrc = resolveClinicLogoSrc(null, meta?.logoUrl);
  const clinicLabel = meta?.clinicName || 'Clinic';

  return (
    <div className="bg-gradient-to-r from-[#425F97] to-[#4f6fb0] px-3 py-2.5 sm:px-4 sm:py-3 text-white">
      <div className="flex items-start gap-2.5 sm:gap-3">
        <div className="shrink-0 rounded-lg bg-white p-1.5 shadow-sm">
          <img
            src={logoSrc}
            alt={clinicLabel}
            className="h-9 w-auto max-w-[5.5rem] object-contain sm:h-10 sm:max-w-[6.5rem]"
          />
        </div>
        <div className="min-w-0 flex-1 pt-0.5">
          <p className="text-[11px] font-medium text-white/75 leading-tight sm:text-xs">{clinicLabel}</p>
          <h1 className="text-base font-semibold leading-tight sm:text-lg">New Inquiry</h1>
          <p className="mt-0.5 text-[11px] leading-snug text-white/80 sm:text-xs">{subtitle}</p>
        </div>
      </div>
    </div>
  );
};

const PublicLeadForm = () => {
  const { token } = useParams();
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [metaError, setMetaError] = useState('');
  const [meta, setMeta] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    guardian_name: '',
    dob: '',
    gender: '',
    contact_primary: '',
    contact_whatsapp: '',
    email: '',
    address: '',
    inquiry_type: 'therapy',
    reference_details: '',
    remarks: '',
    website: '',
  });

  useEffect(() => {
    let cancelled = false;

    const loadMeta = async () => {
      setLoadingMeta(true);
      setMetaError('');
      try {
        const response = await publicLeadsApi.getFormMeta(token);
        if (!cancelled) setMeta(response.data.data);
      } catch (error) {
        if (!cancelled) {
          setMetaError(
            error.response?.data?.message || 'This form link is invalid or no longer available.'
          );
        }
      } finally {
        if (!cancelled) setLoadingMeta(false);
      }
    };

    loadMeta();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');

    try {
      const { website, ...payload } = formData;
      await publicLeadsApi.submit(token, { ...payload, website });
      setSubmitted(true);
    } catch (error) {
      setSubmitError(
        error.response?.data?.message || 'Failed to submit inquiry. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingMeta) {
    return (
      <PageShell>
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="mx-auto h-8 w-8 animate-spin rounded-full border-[3px] border-[#425F97] border-t-transparent" />
            <p className="mt-2 text-xs text-gray-500">Loading...</p>
          </div>
        </div>
      </PageShell>
    );
  }

  if (metaError) {
    return (
      <PageShell>
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <FormCardHeader meta={null} subtitle="This form link is no longer available." />
          <div className="px-3 py-5 text-center sm:px-4">
            <HiExclamationCircle className="mx-auto mb-2 h-10 w-10 text-red-500" />
            <h2 className="text-base font-semibold text-gray-900">Form unavailable</h2>
            <p className="mt-1 text-xs leading-relaxed text-gray-600">{metaError}</p>
          </div>
        </div>
      </PageShell>
    );
  }

  if (submitted) {
    return (
      <PageShell>
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <FormCardHeader meta={meta} subtitle="Your inquiry has been received." />
          <div className="px-3 py-5 text-center sm:px-4">
            <HiCheckCircle className="mx-auto mb-2 h-10 w-10 text-green-500" />
            <h2 className="text-base font-semibold text-gray-900">Thank you!</h2>
            <p className="mt-1 text-xs leading-relaxed text-gray-600">
              Our team at{' '}
              <span className="font-medium text-gray-800">{meta?.clinicName}</span> will contact you
              soon.
            </p>
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <FormCardHeader
          meta={meta}
          subtitle="Fill in your details and we will get back to you."
        />

        <form onSubmit={handleSubmit} className="space-y-3 p-3 sm:space-y-3.5 sm:p-4">
          <input
            type="text"
            name="website"
            value={formData.website}
            onChange={handleChange}
            tabIndex={-1}
            autoComplete="off"
            className="hidden"
            aria-hidden="true"
          />

          <SectionBlock title="Personal Information">
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 sm:gap-3">
              <div className="sm:col-span-2">
                <label className={labelClass}>Full Name *</label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  className={fieldClass}
                  required
                />
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass}>Guardian Name</label>
                <input
                  type="text"
                  name="guardian_name"
                  value={formData.guardian_name}
                  onChange={handleChange}
                  className={fieldClass}
                  placeholder="Parent/guardian name"
                />
              </div>
              <div>
                <label className={labelClass}>Date of Birth</label>
                <input
                  type="date"
                  name="dob"
                  value={formData.dob}
                  onChange={handleChange}
                  className={fieldClass}
                  max={todayYmdIST()}
                />
              </div>
              <div>
                <label className={labelClass}>Gender</label>
                <select name="gender" value={formData.gender} onChange={handleChange} className={fieldClass}>
                  <option value="">Select</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
          </SectionBlock>

          <div className="border-t border-gray-100 pt-3">
            <SectionBlock title="Contact Information">
              <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 sm:gap-3">
                <div>
                  <label className={labelClass}>Primary Contact *</label>
                  <input
                    type="tel"
                    name="contact_primary"
                    value={formData.contact_primary}
                    onChange={handleChange}
                    className={fieldClass}
                    inputMode="tel"
                    autoComplete="tel"
                    required
                  />
                </div>
                <div>
                  <label className={labelClass}>WhatsApp</label>
                  <input
                    type="tel"
                    name="contact_whatsapp"
                    value={formData.contact_whatsapp}
                    onChange={handleChange}
                    className={fieldClass}
                    inputMode="tel"
                    placeholder="Same as primary"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelClass}>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={fieldClass}
                    inputMode="email"
                    autoComplete="email"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelClass}>Address</label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className={`${fieldClass} min-h-[3.25rem] resize-y py-2`}
                    rows={2}
                  />
                </div>
              </div>
            </SectionBlock>
          </div>

          <div className="border-t border-gray-100 pt-3">
            <SectionBlock title="Inquiry Details">
              <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 sm:gap-3">
                <div>
                  <label className={labelClass}>Inquiry For *</label>
                  <select
                    name="inquiry_type"
                    value={formData.inquiry_type}
                    onChange={handleChange}
                    className={fieldClass}
                    required
                  >
                    <option value="therapy">Therapy</option>
                    <option value="assessment">Assessment</option>
                    <option value="special_case">Special Case</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Reference Details</label>
                  <input
                    type="text"
                    name="reference_details"
                    value={formData.reference_details}
                    onChange={handleChange}
                    className={fieldClass}
                    placeholder="Doctor, referrer..."
                  />
                </div>
              </div>
            </SectionBlock>
          </div>

          <div className="border-t border-gray-100 pt-3">
            <SectionBlock title="Remarks">
              <textarea
                name="remarks"
                value={formData.remarks}
                onChange={handleChange}
                className={`${fieldClass} min-h-[3.5rem] w-full resize-y py-2`}
                rows={2}
                placeholder="Additional notes..."
              />
            </SectionBlock>
          </div>

          {submitError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {submitError}
            </div>
          )}

          <div className="border-t border-gray-100 pt-2.5">
            <button
              type="submit"
              disabled={submitting}
              className="btn btn-primary w-full py-2.5 text-sm font-semibold disabled:opacity-60 sm:ml-auto sm:w-auto sm:min-w-[9.5rem] sm:float-right"
            >
              {submitting ? 'Submitting...' : 'Submit Inquiry'}
            </button>
            <div className="clear-both" />
          </div>
        </form>
      </div>
    </PageShell>
  );
};

export default PublicLeadForm;
